import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { applyEach, form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalCheckbox, SignalDropdown, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';
interface RadioGroupConfigModel {
  label: string;
  helpText: string;
  required: boolean;
  options: string[];
  orientation: 'vertical' | 'horizontal';
}

@Component({
  selector: 'app-radio-group-config-view',
  standalone: true,
  imports: [SignalFormField, SignalTextField, SignalCheckbox, SignalDropdown, SignalFormDirective],
  templateUrl: './radio-group-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioGroupConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();
  readonly orientationOptions: {
    value: 'vertical' | 'horizontal';
    label: string;
  }[] = [
    {
      value: 'vertical',
      label: 'Vertical',
    },
    {
      value: 'horizontal',
      label: 'Horizontal',
    },
  ];

  readonly model = signal<RadioGroupConfigModel>({
    label: '',
    helpText: '',
    orientation: 'vertical',
    options: ['', '',''],
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);

    applyEach(schema.options, (option) => {
      required(option);
    });
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        orientation: next.orientation ?? 'vertical',
        options: next.options ?? [],
        required: next.required ?? validation.required ?? false,
      });
    });
    effect(() => {
      let draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      let field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      let updatedField = {
        ...field,
        label: draft.label.trim() || field.label,
        placeholder: undefined,
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        validation: {
          required: draft.required,
        },
        orientation: draft.orientation,
        options: draft.options.map((option) => option.trim()).filter((option) => option.length > 0),
      };
      this.fieldChange.emit(updatedField);
    });
  }

  addOption(): void {
    this.model.update((model) => ({
      ...model,
      options: [...model.options, ''],
    }));
  }

  removeOption(index: number): void {
    this.model.update((model) => ({
      ...model,
      options: model.options.filter((_, i) => i !== index),
    }));
  }

  updateOption(index: number, value: string): void {
    this.model.update((model) => {
      const options = [...model.options];
      options[index] = value;

      return {
        ...model,
        options,
      };
    });
  }
  updateOrientation(value: unknown): void {
    if (value !== 'vertical' && value !== 'horizontal') {
      return;
    }
    this.model.update((model) => ({
      ...model,
      orientation: value,
    }));
  }
}
